import { CommonModule } from '@angular/common';
import { Component, computed, inject, Optional, signal, Signal, WritableSignal } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { MatMenuModule } from '@angular/material/menu';
import { CacheService } from '@app/_services/cache.service';
import { CustomBottomSheetRef } from '@app/_services/custom-bottom-sheet.service';
import { SettingsService } from '@app/_services/settings.service';
import { ViewSettingService } from '@app/_services/view-setting.service';
import { FeatureStore, ViewSettingsStore } from '@app/_stores';
import { ShowType } from '@app/_types/generic.type';
import { InputDropdownComponent } from '@app/components/shared/inputs/input-dropdown/input-dropdown.component';
import { InputToggleComponent } from '@app/components/shared/inputs/input-toggle/input-toggle.component';
import { KebabShowMenuComponent } from '@app/components/shared/kebab-show-menu/kebab-show-menu.component';
import { SvgComponent } from '@app/components/shared/svg/svg.component';
import { Subject, takeUntil } from 'rxjs';

@Component({
  selector: 'app-today-view',
  imports: [
    CommonModule,
    SvgComponent,
    MatMenuModule,
    InputDropdownComponent,
    InputToggleComponent,
    KebabShowMenuComponent
  ],
  templateUrl: './today-view.component.html',
  styleUrl: './today-view.component.scss'
})
export class TodayViewComponent {

  unSubscribe = new Subject<void>();

  constructor(
    @Optional() public dialogRef: MatDialogRef<TodayViewComponent>,
    @Optional() public bottomSheetRef: CustomBottomSheetRef<TodayViewComponent>,
    public ss: SettingsService,
    public vs: ViewSettingService,
    public cc: CacheService
  ) {
    bottomSheetRef?.backdropClick().pipe(takeUntil(this.unSubscribe)).subscribe(async () => {
      this.close()
    });
    dialogRef?.backdropClick().pipe(takeUntil(this.unSubscribe)).subscribe(async () => {
      this.close()
    });
  }

  readonly featureStore = inject(FeatureStore);
  readonly viewSettings = inject(ViewSettingsStore);

  // Create writable signal references for input components
  readonly showCalendarViewSignal: WritableSignal<boolean> = this.ss.viewSignals()['showCalendarView'];
  readonly showUserGoalSignal: WritableSignal<boolean> = this.ss.viewSignals()['showUserGoal'];
  readonly hideCompletedItemsSignal: WritableSignal<boolean> = this.ss.viewSignals()['hideCompletedItems'];
  readonly showTimeboxSignal: WritableSignal<boolean> = this.ss.viewSignals()['showTimebox'];
  readonly entityGroupBySignal: WritableSignal<string> = this.ss.viewSignals()['entityGroupBy'];
  readonly entityShowSignal: WritableSignal<ShowType[]> = this.ss.viewSignals()['entityShow'];

  // Whether invalidEntry toggle should be visible (when entity types with drifted entries are active)
  showInvalidEntryToggle: Signal<boolean> = computed(() => {
    const activeEntities = this.featureStore.activeEntities();
    return activeEntities.includes('habit') || activeEntities.includes('journal') || activeEntities.includes('moneyTracker') || activeEntities.includes('focus');
  });

  // Toggle signal for invalidEntry (derived from ShowType[] array)
  invalidEntryToggle: WritableSignal<boolean> = signal(this.entityShowSignal().includes('invalidEntry'));

  notApplicableValues: Signal<{ value: string, reason: string }[]> = computed(() => {
    const notApplicableValues = [];
    const isGrouped = this.viewSettings.entityGroupBy() !== 'chronological';
    if (isGrouped) {
      notApplicableValues.push({ value: 'label', reason: this.ss.t('viewConfiguration_notApplicableContent') });
    }
    return notApplicableValues;
  });

  hiddenValues: Signal<ShowType[]> = computed(() => {
    const hiddenValues: ShowType[] = ['doneDate', 'date'];
    const activeEntities = this.featureStore.activeEntities();
    const isGrouped = this.viewSettings.entityGroupBy() !== 'chronological';
    if (isGrouped) {
      hiddenValues.push('label');
    }
    if (!activeEntities.includes('habit')) {
      hiddenValues.push('habitResponse');
    }
    if (!activeEntities.includes('todo')) {
      hiddenValues.push('checklist');
    }
    if (!activeEntities.includes('calendarIntegration')) {
      hiddenValues.push('calendarName');
    }
    if (!activeEntities.includes('journal') && !activeEntities.includes('note')) {
      hiddenValues.push('mood');
    }
    if (!activeEntities.includes('moneyTracker')) {
      hiddenValues.push('setup');
    }
    return hiddenValues;
  });

  toggleInvalidEntry() {
    const current = this.entityShowSignal();
    if (this.invalidEntryToggle()) {
      this.entityShowSignal.set([...current, 'invalidEntry']);
    } else {
      this.entityShowSignal.set(current.filter((item: string) => item !== 'invalidEntry'));
    }
    this.ss.updateView();
  }

  closeDialog() {
    this.close();
  }

  private close(result?: any): void {
    if (this.dialogRef) {
      this.dialogRef.close(result);
    } else if (this.bottomSheetRef) {
      this.bottomSheetRef.dismiss(result);
    }
  }

}
